import { Check, NotePencil, MicrophoneStage, ImageSquare, Headphones, Share } from "@phosphor-icons/react";

const STEPS = [
  { key: 1, label: "Tu historia", short: "Historia", Icon: NotePencil },
  { key: 2, label: "Revisa la letra", short: "Letra", Icon: MicrophoneStage },
  { key: 3, label: "Tu portada", short: "Portada", Icon: ImageSquare },
  { key: 4, label: "Escucha tu canción", short: "Escucha", Icon: Headphones },
  { key: 5, label: "Comparte / Descarga", short: "Compartir", Icon: Share },
];

/**
 * Visual progress bar for the create-song flow.
 *
 * Props:
 * - currentStep: 1..5
 * - onStepClick(stepKey): called when a step that's <= reachableMax is clicked
 * - reachableMax: the highest step number the user can navigate back/forward to
 *   (e.g. if song generation finished, all 5 are reachable; if still on step 1, only 1)
 */
export default function CreateProgress({ currentStep, onStepClick, reachableMax }) {
  return (
    <div className="mb-6 sm:mb-8" data-testid="create-progress-bar">
      <div className="flex items-start gap-1 overflow-x-auto no-scrollbar -mx-1 px-1 pb-2">
        {STEPS.map((s, idx) => {
          const isCompleted = currentStep > s.key;
          const isActive = currentStep === s.key;
          const isReachable = s.key <= (reachableMax ?? currentStep);
          const Icon = isCompleted ? Check : s.Icon;
          return (
            <div key={s.key} className="flex items-start flex-shrink-0">
              <button
                type="button"
                disabled={!isReachable || !onStepClick}
                onClick={() => isReachable && onStepClick?.(s.key)}
                data-testid={`progress-step-${s.key}`}
                className="flex flex-col items-center gap-1.5 group disabled:cursor-default min-w-[56px] sm:min-w-[80px]"
              >
                <div
                  className={`relative w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isActive
                      ? "bg-[#FFB800] text-black ring-4 ring-[#FFB800]/25 scale-110"
                      : isCompleted
                      ? "bg-[#B4F34A] text-black"
                      : isReachable
                      ? "bg-white/10 text-white/60 group-hover:bg-white/15"
                      : "bg-white/[0.03] text-white/20"
                  }`}
                >
                  <Icon size={isActive ? 18 : 16} weight={isCompleted || isActive ? "fill" : "bold"} />
                  {isActive && (
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#FFB800] animate-pulse"></span>
                  )}
                </div>
                <span
                  className={`text-[10px] sm:text-xs font-bold text-center leading-tight ${
                    isActive
                      ? "text-[#FFB800]"
                      : isCompleted
                      ? "text-[#B4F34A]"
                      : isReachable
                      ? "text-white/70"
                      : "text-white/30"
                  }`}
                >
                  {s.short}
                </span>
              </button>
              {idx < STEPS.length - 1 && (
                <div
                  className={`h-[2px] w-6 sm:w-10 mt-[18px] sm:mt-[22px] mx-1 sm:mx-1.5 rounded-full transition-colors duration-300 ${
                    isCompleted ? "bg-[#B4F34A]" : "bg-white/10"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
